/**
 * Mock data for Mission Detail
 */

import type { MissionDetail } from '../../types/mission';
import { MissionStatus } from '../../types/mission';
import { generateId, randomElement } from './mockData';
import { mockMissions } from './mockMission';

const formatDateTime = (date: Date): string => {
  return date.toISOString();
};

const missionTypes: Array<'QuickHit' | 'LightSurveillance' | 'FullInvestigative'> = [
  'QuickHit',
  'LightSurveillance',
  'FullInvestigative',
] as const;

const missionTypeLabels: Record<string, string> = {
  QuickHit: 'Quick Hit',
  LightSurveillance: 'Light Surveillance',
  FullInvestigative: 'Full Investigative',
};

const categoryNames: Record<string, string> = {
  QuickHit: 'Quick Hit',
  LightSurveillance: 'Light Surveillance',
  FullInvestigative: 'Full Investigative',
};

const subcategoryNames: Record<string, string[]> = {
  QuickHit: ['Quick Photo', 'Location Confirmation', 'Presence Verification'],
  LightSurveillance: ['Physical Surveillance', 'Digital Surveillance', 'Location Tracking'],
  FullInvestigative: ['Background Check', 'Asset Search', 'Social Media Investigation'],
};

const users = [
  { id: 'USR-J5D9', name: 'John Doe' },
  { id: 'USR-K8F2', name: 'Jane Smith' },
  { id: 'USR-L1G5', name: 'Sam Wilson' },
  { id: 'USR-M4H8', name: 'Emily Brown' },
  { id: 'USR-N7I1', name: 'Chris Green' },
  { id: 'USR-P2J4', name: 'Alex Johnson' },
  { id: 'USR-Q5K7', name: 'Maria Garcia' },
  { id: 'USR-R8L0', name: 'David Lee' },
];

const ghosts = [
  { id: 'GST-42', name: 'Jane Smith', tier: 'Junior' as const },
  { id: 'GST-15', name: 'Michael Chen', tier: 'Senior' as const },
  { id: 'GST-28', name: 'Sarah Johnson', tier: 'Elite' as const },
  { id: 'GST-33', name: 'Robert Williams', tier: 'Junior' as const },
  { id: 'GST-51', name: 'Lisa Anderson', tier: 'Senior' as const },
];

const locations = [
  {
    address: '123 Main Street',
    city: 'New York',
    state: 'NY',
    country: 'USA',
    zipCode: '10001',
    coordinates: { latitude: 40.7128, longitude: -74.0060 },
  },
  {
    address: '456 Oak Avenue',
    city: 'Los Angeles',
    state: 'CA',
    country: 'USA',
    zipCode: '90001',
    coordinates: { latitude: 34.0522, longitude: -118.2437 },
  },
  {
    address: '789 Pine Road',
    city: 'Chicago',
    state: 'IL',
    country: 'USA',
    zipCode: '60601',
    coordinates: { latitude: 41.8781, longitude: -87.6298 },
  },
];





const generateCategoryAnswers = (missionType: string) => {
  const answers: Record<string, any> = {};

  switch (missionType) {
    case 'QuickHit':
      answers['What is the target location?'] = 'Downtown area, near the shopping mall';
      answers['What time frame are you looking for?'] = 'Within the next 2 hours';
      answers['What specific information do you need?'] = 'Photo confirmation of presence';
      answers['Are there any special requirements?'] = 'Need to be discreet';
      break;
    case 'LightSurveillance':
      answers['What is the surveillance objective?'] = 'Monitor daily activities and movements';
      answers['What is the duration of surveillance?'] = '7 days';
      answers['What locations should be monitored?'] = 'Home and workplace';
      answers['What specific activities are you looking for?'] = 'Daily routine and interactions';
      answers['What time periods are most important?'] = 'Morning commute and evening hours';
      break;
    case 'FullInvestigative':
      answers['What is the investigation objective?'] = 'Comprehensive background investigation';
      answers['What areas should be investigated?'] = 'Employment history, financial records, and social connections';
      answers['What is the time frame for the investigation?'] = 'Last 5 years';
      answers['Are there any specific concerns or red flags?'] = 'Possible financial irregularities';
      answers['What level of detail is required?'] = 'Complete comprehensive report with evidence';
      break;
    case 'social_media_scan':
      answers['Which platforms should be investigated?'] = 'Facebook, Instagram, Twitter, LinkedIn';
      answers['What is the subject\'s username?'] = '@johndoe123';
      answers['What time period should be covered?'] = 'Last 6 months';
      answers['What type of content are you looking for?'] = 'Posts, comments, photos, and activity';
      break;
    case 'data_recovery':
      answers['What type of device?'] = 'Laptop hard drive';
      answers['What caused the data loss?'] = 'Accidental deletion';
      answers['What type of files need to be recovered?'] = 'Documents, photos, and videos';
      answers['How long ago was the data lost?'] = '2 weeks ago';
      break;
    case 'surveillance':
      answers['What is the surveillance objective?'] = 'Monitor daily activities';
      answers['What is the duration of surveillance?'] = '7 days';
      answers['What locations should be monitored?'] = 'Home and workplace';
      answers['What specific activities are you looking for?'] = 'Daily routine and interactions';
      break;
    default:
      answers['Question 1'] = 'Answer 1';
      answers['Question 2'] = 'Answer 2';
  }

  return answers;
};

const generateEvidence = (_missionType: 'QuickHit' | 'LightSurveillance' | 'FullInvestigative', createdDate: Date) => {
  const evidence = [];
  const evidenceCount = Math.floor(Math.random() * 5) + 3; // 3-7 items

  // Use placeholder services for images and videos
  const imageWidths = [800, 1024, 1200, 1600];
  const imageHeights = [600, 768, 900, 1200];
  const videoIds = [1, 2, 3, 4, 5];

  for (let i = 0; i < evidenceCount; i++) {
    const types: Array<'photo' | 'video' | 'file'> = ['photo', 'video', 'file'];
    const type = randomElement(types);
    const uploadedAt = new Date(createdDate.getTime() + (i + 1) * 2 * 60 * 60 * 1000);

    if (type === 'photo') {
      const width = randomElement(imageWidths);
      const height = randomElement(imageHeights);
      const imageId = Math.floor(Math.random() * 1000) + 1;
      // Use Picsum Photos for placeholder images
      const imageUrl = `https://picsum.photos/id/${imageId}/${width}/${height}`;
      const thumbnailUrl = `https://picsum.photos/id/${imageId}/400/300`;

      evidence.push({
        id: generateId(),
        type: 'photo' as const,
        url: imageUrl,
        thumbnailUrl: thumbnailUrl,
        fileName: `evidence-photo-${i + 1}.jpg`,
        fileSize: Math.floor(Math.random() * 3000000) + 1000000, // 1-4 MB
        mimeType: 'image/jpeg',
        uploadedAt,
        uploadedAtText: formatDateTime(uploadedAt),
        uploadedBy: randomElement(ghosts).name,
      });
    } else if (type === 'video') {
      const videoId = randomElement(videoIds);
      // Use Google's sample videos bucket for placeholder videos
      const videoUrls = [
        'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4',
        'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ElephantsDream.mp4',
        'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4',
        'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerEscapes.mp4',
        'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerFun.mp4',
      ];
      const videoUrl = videoUrls[(i - 1) % videoUrls.length];
      const thumbnailUrl = `https://picsum.photos/id/${videoId * 100 + i}/400/300`;

      evidence.push({
        id: generateId(),
        type: 'video' as const,
        url: videoUrl,
        thumbnailUrl: thumbnailUrl,
        fileName: `evidence-video-${i + 1}.mp4`,
        fileSize: Math.floor(Math.random() * 50000000) + 10000000, // 10-60 MB
        mimeType: 'video/mp4',
        uploadedAt,
        uploadedAtText: formatDateTime(uploadedAt),
        uploadedBy: randomElement(ghosts).name,
      });
    } else {
      const fileTypes = [
        { name: 'report', ext: 'pdf', mime: 'application/pdf', size: 512000 },
        { name: 'document', ext: 'docx', mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', size: 256000 },
        { name: 'spreadsheet', ext: 'xlsx', mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', size: 128000 },
      ];
      const fileType = randomElement(fileTypes);
      evidence.push({
        id: generateId(),
        type: 'file' as const,
        url: `#`, // Files don't have real URLs in mock data
        fileName: `${fileType.name}-${i + 1}.${fileType.ext}`,
        fileSize: fileType.size,
        mimeType: fileType.mime,
        uploadedAt,
        uploadedAtText: formatDateTime(uploadedAt),
        uploadedBy: randomElement(ghosts).name,
      });
    }
  }

  return evidence;
};

const generateAttachments = (createdDate: Date, user: typeof users[0]) => {
  const attachments = [];
  const attachmentCount = Math.floor(Math.random() * 3) + 2; // 2-4 items

  const fileTypes = [
    { name: 'initial-request', ext: 'pdf', mime: 'application/pdf', size: 256000 },
    { name: 'supporting-documents', ext: 'zip', mime: 'application/zip', size: 1024000 },
    { name: 'reference-letter', ext: 'pdf', mime: 'application/pdf', size: 128000 },
    { name: 'identification', ext: 'pdf', mime: 'application/pdf', size: 512000 },
  ];

  for (let i = 0; i < attachmentCount; i++) {
    const fileType = fileTypes[i % fileTypes.length];
    attachments.push({
      id: generateId(),
      fileName: `${fileType.name}-${i + 1}.${fileType.ext}`,
      fileSize: fileType.size,
      mimeType: fileType.mime,
      url: `/mock-attachments/${fileType.name}-${i + 1}.${fileType.ext}`,
      uploadedAt: new Date(createdDate.getTime() + i * 60 * 60 * 1000),
      uploadedAtText: formatDateTime(new Date(createdDate.getTime() + i * 60 * 60 * 1000)),
      uploadedBy: user.name,
    });
  }

  return attachments;
};

const generateStatusHistory = (status: string, createdDate: Date, updatedDate: Date, user: typeof users[0], ghost?: typeof ghosts[0]) => {
  const history = [];

  // Always start with Pending
  history.push({
    status: MissionStatus.PENDING,
    timestamp: createdDate,
    timestampText: formatDateTime(createdDate),
    description: `Mission created by ${user.name}`,
    userId: user.id,
    userName: user.name,
  });

  // Add Submitted if not Pending
  if (status !== MissionStatus.PENDING) {
    const submittedDate = new Date(createdDate.getTime() + 1 * 60 * 60 * 1000);
    history.push({
      status: MissionStatus.SUBMITTED,
      timestamp: submittedDate,
      timestampText: formatDateTime(submittedDate),
      description: `Mission submitted by ${user.name}`,
      userId: user.id,
      userName: user.name,
    });
  }

  // Add GhostAssigned if ghost is assigned
  if (ghost && status !== MissionStatus.PENDING && status !== MissionStatus.SUBMITTED) {
    const assignedDate = new Date(createdDate.getTime() + 2 * 60 * 60 * 1000);
    history.push({
      status: MissionStatus.GHOST_ASSIGNED,
      timestamp: assignedDate,
      timestampText: formatDateTime(assignedDate),
      description: `Ghost ${ghost.name} assigned to mission`,
      userId: 'SYSTEM',
      userName: 'System',
    });
  }

  // Add MissionInProgress if status is MissionInProgress or later
  if (status === MissionStatus.MISSION_IN_PROGRESS || status === MissionStatus.COMPLETED || status === MissionStatus.UNDER_REVIEW) {
    const inProgressDate = new Date(createdDate.getTime() + 3 * 60 * 60 * 1000);
    history.push({
      status: MissionStatus.MISSION_IN_PROGRESS,
      timestamp: inProgressDate,
      timestampText: formatDateTime(inProgressDate),
      description: ghost ? `Mission started by ${ghost.name}` : 'Mission started by System',
      userId: ghost?.id || 'SYSTEM',
      userName: ghost?.name || 'System',
    });
  }

  // Add current status if Completed or UnderReview
  if (status === MissionStatus.COMPLETED || status === MissionStatus.UNDER_REVIEW) {
    const statusDescriptions: Record<string, string> = {
      Completed: `Mission completed by ${ghost?.name || user.name}`,
      UnderReview: `Mission flagged for admin review`,
    };
    history.push({
      status: status as any,
      timestamp: updatedDate,
      timestampText: formatDateTime(updatedDate),
      description: statusDescriptions[status] || `Mission ${status}`,
      userId: ghost?.id || user.id,
      userName: ghost?.name || user.name,
    });
  }

  return history.reverse(); // Reverse to show newest first
};

const generateActivities = (mission: MissionDetail, createdDate: Date, updatedDate: Date, user: typeof users[0], ghost?: typeof ghosts[0]) => {
  const activities = [];

  // Created
  activities.push({
    id: generateId(),
    type: 'created' as const,
    description: `Mission created by ${user.name}`,
    timestamp: createdDate,
    timestampText: formatDateTime(createdDate),
    userId: user.id,
    userName: user.name,
  });

  // Assigned
  if (ghost) {
    activities.push({
      id: generateId(),
      type: 'assigned' as const,
      description: `Mission assigned to ${ghost.name}`,
      timestamp: new Date(createdDate.getTime() + 2 * 60 * 60 * 1000),
      timestampText: formatDateTime(new Date(createdDate.getTime() + 2 * 60 * 60 * 1000)),
      userId: 'SYSTEM',
      userName: 'System',
    });
  }

  // Status changes
  if (mission.status !== MissionStatus.PENDING) {
    const statusLabels: Record<string, string> = {
      Submitted: 'Submitted / Connecting with Ghost',
      GhostAssigned: 'Ghost Assigned',
      MissionInProgress: 'Mission In Progress',
      [MissionStatus.COMPLETED]: 'Completed',
      UnderReview: 'Under Review',
    };
    activities.push({
      id: generateId(),
      type: 'status_changed' as const,
      description: `Status changed to ${statusLabels[mission.status] || mission.status}`,
      timestamp: new Date(createdDate.getTime() + 24 * 60 * 60 * 1000),
      timestampText: formatDateTime(new Date(createdDate.getTime() + 24 * 60 * 60 * 1000)),
      userId: ghost?.id || user.id,
      userName: ghost?.name || user.name,
      metadata: { from: MissionStatus.PENDING, to: mission.status },
    });
  }

  // File uploads
  if (mission.evidence && mission.evidence.length > 0) {
    mission.evidence.forEach((evidence) => {
      if (evidence.fileName) {
        activities.push({
          id: generateId(),
          type: 'file_uploaded' as const,
          description: `${evidence.type === 'photo' ? 'Evidence photo' : evidence.type === 'video' ? 'Evidence video' : 'Evidence file'} uploaded: ${evidence.fileName}`,
          timestamp: evidence.uploadedAt,
          timestampText: evidence.uploadedAtText,
          userId: ghost?.id || user.id,
          userName: evidence.uploadedBy || ghost?.name || user.name,
          metadata: { fileName: evidence.fileName, fileType: evidence.type },
        });
      }
    });
  }

  // Final status change if completed
  if (mission.status === MissionStatus.COMPLETED) {
    activities.push({
      id: generateId(),
      type: 'status_changed' as const,
      description: 'Status changed to Completed',
      timestamp: updatedDate,
      timestampText: formatDateTime(updatedDate),
      userId: ghost?.id || user.id,
      userName: ghost?.name || user.name,
      metadata: { from: MissionStatus.MISSION_IN_PROGRESS, to: MissionStatus.COMPLETED },
    });
  }

  return activities;
};

// Generate detailed mock mission data
export const generateMockMissionDetail = (missionId: string): MissionDetail | null => {
  // First, try to find the mission in mockMissions to ensure consistency
  const existingMission = mockMissions.find(
    m => m.id === missionId || m.code === missionId
  );

  const now = new Date();
  let createdDate: Date;
  let updatedDate: Date;
  let user: typeof users[0];
  let missionType: 'QuickHit' | 'LightSurveillance' | 'FullInvestigative';
  let status: MissionStatus;

  if (existingMission) {
    // Use data from existing mission to ensure consistency
    createdDate = new Date(existingMission.createdAt);
    updatedDate = new Date(existingMission.updatedAt);
    user = { id: existingMission.assetId, name: `${existingMission.assetFirstName} ${existingMission.assetLastName}` };
    missionType = existingMission.missionType;
    status = existingMission.status;
  } else {
    // Fallback: generate random data if mission not found
    const daysAgo = Math.floor(Math.random() * 30) + 1;
    createdDate = new Date(now.getTime() - daysAgo * 24 * 60 * 60 * 1000);
    updatedDate = new Date(createdDate.getTime() + Math.random() * (daysAgo - 1) * 24 * 60 * 60 * 1000);
    user = randomElement(users);
    missionType = randomElement(missionTypes);
    const statuses: MissionStatus[] = [
      MissionStatus.PENDING,
      MissionStatus.PENDING_ADMIN_REVIEW,
      MissionStatus.SUBMITTED,
      MissionStatus.GHOST_ASSIGNED,
      MissionStatus.MISSION_IN_PROGRESS,
      MissionStatus.COMPLETED,
      MissionStatus.UNDER_REVIEW,
    ];
    status = randomElement(statuses);
  }

  const ghost = Math.random() > 0.3 ? randomElement(ghosts) : undefined;

  const location = randomElement(locations);
  const subcategories = subcategoryNames[missionType] || ['General'];
  const subcategory = randomElement(subcategories);

  const basePrice = Math.floor(Math.random() * 500) + 200; // 200-700
  const platformFee = Math.floor(basePrice * 0.15);
  const totalPrice = basePrice + platformFee;

  const mission: MissionDetail = {
    id: existingMission?.id || generateId(),
    code: existingMission?.code || `CODE-${(existingMission?.id || missionId).substring(0, 5)}`,
    title: existingMission?.title || `${missionTypeLabels[missionType]} Mission`,
    missionType,
    category: existingMission?.category || 'General',
    status,
    createdAt: createdDate.toISOString(),
    updatedAt: updatedDate.toISOString(),
    assetId: existingMission?.assetId || user.id,
    assetFirstName: existingMission?.assetFirstName || user.name.split(' ')[0],
    assetLastName: existingMission?.assetLastName || user.name.split(' ')[1] || '',
    assetCallSign: existingMission?.assetCallSign || `${user.name.split(' ')[0]}-Asset`,
    assetEmail: existingMission?.assetEmail || `${user.name.toLowerCase().replace(' ', '.')}@example.com`,
    ghostId: [MissionStatus.PENDING, MissionStatus.PENDING_ADMIN_REVIEW, MissionStatus.SUBMITTED].includes(status) ? undefined : (existingMission?.ghostId || ghost?.id),
    ghostFirstName: [MissionStatus.PENDING, MissionStatus.PENDING_ADMIN_REVIEW, MissionStatus.SUBMITTED].includes(status) ? undefined : (existingMission?.ghostFirstName || 'Ghost'),
    ghostLastName: [MissionStatus.PENDING, MissionStatus.PENDING_ADMIN_REVIEW, MissionStatus.SUBMITTED].includes(status) ? undefined : (existingMission?.ghostLastName || 'One'),
    ghostCallSign: [MissionStatus.PENDING, MissionStatus.PENDING_ADMIN_REVIEW, MissionStatus.SUBMITTED].includes(status) ? undefined : (existingMission?.ghostCallSign || ghost?.name || 'Specter'),
    ghostEmail: [MissionStatus.PENDING, MissionStatus.PENDING_ADMIN_REVIEW, MissionStatus.SUBMITTED].includes(status) ? undefined : (existingMission?.ghostEmail || 'ghost@example.com'),

    // Basic Info
    description: `This is a comprehensive ${missionTypeLabels[missionType].toLowerCase()} mission. The investigation covers all relevant aspects and requirements specified in the mission details.`,

    // Location & Timeframe
    location: {
      ...location,
    },
    timeframe: {
      startDate: createdDate.toISOString().split('T')[0],
      startTime: '09:00',
      endDate: new Date(createdDate.getTime() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      endTime: '18:00',
      deadline: new Date(createdDate.getTime() + 14 * 24 * 60 * 60 * 1000).toISOString(),
    },

    // User Info (legacy or detail fields)
    assetName: existingMission ? `${existingMission.assetFirstName} ${existingMission.assetLastName}` : user.name,
    ghostName: existingMission ? (existingMission.ghostFirstName ? `${existingMission.ghostFirstName} ${existingMission.ghostLastName}` : undefined) : ghost?.name,
    ghostTier: ghost?.tier,

    // Category & Questions
    categoryId: `CAT-${missionType.toUpperCase().slice(0, 3)}001`,
    subcategoryId: `SUBCAT-${missionType.toUpperCase().slice(0, 3)}001-01`,
    categoryName: categoryNames[missionType],
    subcategoryName: subcategory,
    categoryAnswers: generateCategoryAnswers(missionType),

    // Assignee
    assignee: ghost ? {
      id: ghost.id,
      name: ghost.name,
      avatar: undefined,
    } : undefined,

    // Evidence & Attachments
    evidence: generateEvidence(missionType, createdDate),
    attachments: generateAttachments(createdDate, user),

    // Report (only if completed)
    report: status === MissionStatus.COMPLETED ? {
      id: generateId(),
      content: `This is a comprehensive ${missionTypeLabels[missionType].toLowerCase()} report. The investigation has been completed successfully with all findings documented in detail. All evidence has been collected and verified. The mission objectives have been met and the final report is ready for review.`,
      summary: `${missionTypeLabels[missionType]} completed successfully. All objectives met.`,
      createdAt: updatedDate,
      createdAtText: updatedDate.toISOString(),
      createdBy: ghost?.name || user.name,
      downloadUrl: `/mock-reports/report-${missionId}.pdf`,
    } : undefined,

    // Report sections (for Debrief Tab)
    reportWeThink: status === MissionStatus.COMPLETED ? `Based on our investigation, we believe the subject has been engaging in the activities as described. The evidence collected supports this conclusion.` : undefined,
    reportConfidence: status === MissionStatus.COMPLETED ? Math.floor(Math.random() * 20) + 80 : undefined, // 80-100%
    reportHereWhy: status === MissionStatus.COMPLETED ? `The evidence collected includes multiple photos and videos that clearly show the subject's activities. The timestamps and locations match the reported information. All findings have been verified and documented.` : undefined,

    reviewDecision: undefined,
    reviewReason: undefined,
    canApproveReview: status === MissionStatus.PENDING_ADMIN_REVIEW,
    canRejectReview: status === MissionStatus.PENDING_ADMIN_REVIEW,
    latestPaymentStatus: status === MissionStatus.PENDING_ADMIN_REVIEW ? 'Authorized' : undefined,

    // Status History
    statusHistory: generateStatusHistory(status, createdDate, updatedDate, user, ghost),

    // Activity History (will be generated after mission is created)
    activities: [],

    // Pricing
    price: totalPrice,
    basePrice,
    platformFee,
    totalPrice,

    // Other
    estimatedCompletionHours: Math.floor(Math.random() * 72) + 24, // 24-96 hours
    confidence: status === MissionStatus.COMPLETED ? Math.floor(Math.random() * 20) + 80 : undefined, // 80-100% if completed
  };

  // Generate activities after mission is created
  mission.activities = generateActivities(mission, createdDate, updatedDate, user, ghost);

  return mission;
};
